import React, { ComponentProps } from "react";
import { Button, Icon } from "@clarkinc/zest-design-system/ui";
import { cls } from "@wss/cls";

const highlightClassNames =
  "bg-green-50 outline outline-green-600 outline-[3px] outline-offset-[3px]".split(
    " ",
  );
const transitionClassNames =
  "bg-white outline-transparent transition-all duration-[3s] ease-out".split(
    " ",
  );

const highlightElement = (element: HTMLElement | null) => {
  if (!element) return;

  element.classList.add(...highlightClassNames);
  setTimeout(() => {
    element.classList.remove("bg-green-50");
    element.classList.add(...transitionClassNames);
  }, 1);
  setTimeout(() => {
    element.classList.remove(...highlightClassNames, ...transitionClassNames);
  }, 3000);
};

interface NavigationProps extends ComponentProps<"div"> {
  reviewCount: number;
  replacementEligible: boolean;
  minimal: boolean;
}

const Navigation = ({
  reviewCount,
  replacementEligible,
  minimal,
}: NavigationProps) => {
  const links = [
    {
      area: "#details-group",
      text: "Details",
      show: () => !!window.document?.querySelector("#details-group"),
      onClick: () =>
        window.document
          ?.querySelector<HTMLElement>("#details-group")
          ?.scrollIntoView(),
    },
    {
      area: "#resources-group",
      text: (
        <span className="before:content-['Resources'] lg:before:content-['Resources_&_Downloads']" />
      ),
      show: () => !!window.document?.querySelector("#resources-group"),
      onClick: () => {
        const element =
          window.document?.querySelector<HTMLElement>("#resources-group");
        element?.scrollIntoView();
        highlightElement(element);
      },
    },
    {
      area: "#specifications-group",
      text: (
        <span className="before:content-['Specifications'] lg:before:content-['Specifications_&_Dimensions']" />
      ),
      show: () => !!window.document?.querySelector("#specifications-group"),
      onClick: () => {
        const element = window.document?.querySelector<HTMLElement>(
          "#specifications-group",
        );
        element?.scrollIntoView();
        highlightElement(element);
      },
    },
    {
      area: "#customerQA",
      title: "Customer Q&A",
      text: "Questions & Answers",
      show: () =>
        !!window.document?.querySelector("#customerQA") && !replacementEligible,
      onClick: () =>
        window.document
          ?.querySelector<HTMLElement>("#customerQA")
          ?.scrollIntoView(),
    },
    {
      area: "#compare",
      text: "Compare",
      show: () => !!window.document?.querySelector("#compare"),
      onClick: () =>
        window.document
          ?.querySelector<HTMLElement>("#compare")
          ?.scrollIntoView(),
    },
    {
      area: "#review-section",
      title: "Customer Reviews",
      text: (
        <>
          Reviews
          <span className="text-gray-700 inline-block -mb-[1px] ml-1/2 text-[80%] opacity-80">
            ({reviewCount})
          </span>
        </>
      ),
      show: () =>
        !!window.document?.querySelector("#review-section") && reviewCount > 0,
      onClick: () =>
        window.document
          ?.querySelector<HTMLElement>("#review-section")
          ?.scrollIntoView(),
    },
  ];

  return (
    <div className="container flex justify-between before:block after:block">
      <ul className="list-none mb-0 mx-0" aria-label="quick links">
        {links
          .filter((e) => e.show())
          .map((e) => (
            <li className="inline h-full" key={e.area}>
              <Button
                variant="plain"
                className={cls(
                  "bg-transparent !text-sm-3/4 font-semibold py-2 px-3 border-b-[3px] border-b-transparent border-solid border-t-0 border-x-0 inline-flex items-center align-baseline scroll-anchor text-gray-800 transition-all duration-200",
                  "hover:border-green-500 hover:border-b-[3px] hover:border-solid hover:no-underline",
                )}
                onClick={e.onClick}
              >
                {e.text}
              </Button>
            </li>
          ))}
      </ul>
      {!minimal && (
        <Button
          variant="plain"
          className={cls(
            "bg-transparent border-0 flex align-baseline items-center py-2 mr-4 text-sm font-semibold text-gray-800 scroll-anchor transition-all duration-200",
            "!hover:underline hover:border-gray-400",
            "lg:mr-0",
          )}
          onClick={() => document?.querySelector("#td")?.scrollIntoView()}
        >
          <span className="mr-2">Back To Top</span>
          <Icon name="ri-arrow-up-line" className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default Navigation;
